<template>
  <div v-if="currentState" class="global-loading-overlay" :class="overlayClass">
    <div class="loading-container">
      <!-- 加载动画 -->
      <div class="loading-spinner" :class="spinnerClass">
        <div class="spinner-ring"></div>
        <div class="spinner-center"></div>
      </div>

      <!-- 加载信息 -->
      <div class="loading-content">
        <div class="loading-message">{{ currentState.message }}</div>
        <div v-if="showProgress" class="loading-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <span class="progress-text">{{ progressPercent }}%</span>
        </div>
        <div v-if="showStats" class="loading-stats">
          <small>正在处理 {{ totalLoading }} 个请求</small>
        </div>
      </div>

      <!-- 取消按钮 -->
      <button v-if="cancellable" @click="cancelLoading" class="cancel-btn">
        <i class="fas fa-times"></i>
        取消
      </button>
    </div>
  </div>
</template>

<script>
import loadingManager from '@/utils/loading-manager'

export default {
  name: 'GlobalLoading',
  data() {
    return {
      currentState: null,
      allStates: [],
      globalState: false,
      progress: 0,
      cancellable: false
    }
  },
  computed: {
    overlayClass() {
      const classes = []
      if (this.currentState) {
        classes.push(`priority-${this.currentState.priority}`)
        classes.push(`type-${this.currentState.type}`)
      }
      return classes
    },
    spinnerClass() {
      return this.currentState ? `spinner-${this.currentState.type}` : ''
    },
    showProgress() {
      return this.currentState && this.currentState.type === 'data'
    },
    showStats() {
      return this.allStates.length > 1
    },
    progressPercent() {
      return Math.min(100, Math.max(0, this.progress))
    },
    totalLoading() {
      return this.allStates.length
    }
  },
  mounted() {
    // 监听加载状态变化
    loadingManager.addListener(this.handleLoadingChange)

    // 启动进度模拟（如果需要）
    if (this.showProgress) {
      this.startProgressSimulation()
    }
  },
  beforeDestroy() {
    loadingManager.removeListener(this.handleLoadingChange)
  },
  methods: {
    handleLoadingChange(status) {
      this.currentState = status.currentState
      this.allStates = status.allStates
      this.globalState = status.globalState

      // 根据加载类型设置是否可取消
      this.cancellable = this.currentState &&
                        (this.currentState.type === 'data' || this.currentState.type === 'page')

      // 重置进度
      if (this.currentState && this.currentState.type === 'data') {
        this.startProgressSimulation()
      }
    },

    startProgressSimulation() {
      this.progress = 0
      const interval = setInterval(() => {
        if (!this.currentState) {
          clearInterval(interval)
          return
        }

        // 模拟进度增长（越来越慢）
        const increment = Math.random() * 15 * (1 - this.progress / 100)
        this.progress = Math.min(95, this.progress + increment)

        if (this.progress >= 95) {
          clearInterval(interval)
        }
      }, 200)
    },

    cancelLoading() {
      if (this.currentState && this.cancellable) {
        // 取消当前加载
        loadingManager.endLoading(this.currentState.key)
        this.$message.info('已取消加载')
      }
    }
  }
}
</script>

<style scoped>
.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

.global-loading-overlay.priority-high {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
}

.global-loading-overlay.type-overlay {
  background: rgba(0, 0, 0, 0.7);
}

.global-loading-overlay.type-page {
  background: rgba(255, 255, 255, 0.8);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  min-width: 280px;
  max-width: 400px;
}

.global-loading-overlay.type-overlay .loading-container {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* 加载动画 */
.loading-spinner {
  margin-bottom: 24px;
  position: relative;
}

.spinner-ring {
  width: 48px;
  height: 48px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #409EFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: #409EFF;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

.spinner-data .spinner-ring {
  border-top-color: #67C23A;
}

.spinner-page .spinner-ring {
  border-top-color: #E6A23C;
  width: 56px;
  height: 56px;
}

.spinner-page .spinner-center {
  width: 20px;
  height: 20px;
  background: #E6A23C;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(0.8);
  }
}

/* 加载内容 */
.loading-content {
  text-align: center;
  margin-bottom: 16px;
}

.loading-message {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 8px;
}

.global-loading-overlay.type-overlay .loading-message {
  color: #fff;
}

.loading-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: #e4e7ed;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #409EFF, #66b1ff);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 500;
  color: #409EFF;
  min-width: 35px;
}

.loading-stats {
  margin-top: 8px;
}

.loading-stats small {
  color: #909399;
  font-size: 12px;
}

/* 取消按钮 */
.cancel-btn {
  padding: 6px 16px;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.cancel-btn:hover {
  border-color: #c0c4cc;
  background: #f5f7fa;
  color: #409EFF;
}

.cancel-btn:active {
  transform: scale(0.95);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .loading-container {
    padding: 24px;
    min-width: 240px;
    max-width: 320px;
  }

  .loading-message {
    font-size: 15px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>